import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import get from 'lodash/get';
import { cnUtils, CnBox } from '@cainiaofe/cn-ui';
import {
  getLowCodeCoreUtil,
  handleFormatChildren,
  isArrayNotEmpty,
  isDesignMode,
  makeDesignContext,
  setDataToDs,
  initArray,
  calculateFeature,
} from '@/lowcode/common/util/util';
import { CnStatisticCardPro } from '@/components/cn-statistic-card-pro/index-onecode-pc';
import { useData } from '@/lowcode/common/hooks/useData';
import useFilterSearchListener, {
  CnFilterDefaultValueFinished,
  CnFilterOnSearch,
  emitEvent,
} from '../common/hooks/useFilterSearchListener';
import {
  __statisticCard_activeKey__,
  __statisticCard_clickItem__,
  __statisticCard_currentItem__,
} from '@/lowcode/common/util/expr-const';
import { getFormattedValue } from '@/lowcode/common/comoponents/util';
import { FeaturePosition } from '@/types/feature-position';
import ViewWrapper from '@/lowcode/common/comoponents/view-wrapper';
import WrapperContainer from '../common/wrapper-container';
import Skeleton from './skeleton';
import { CnStatisticCardListContext } from './context';
import './view.scss';
import useStopLoop from '@/lowcode/common/hooks/useStopLoop';

const { HocBaseComponents, useCreatePluginInstance } = cnUtils || {};

const CnStatisticCardListView = forwardRef((props: any, ref) => {
  const {
    children,
    dataFrom,
    filterConfig,
    skeleton = {},
    oneCodePc = {},
    dataFilterFun,
    blocks = {},
    events,
    _dataSourceName,
    _dataSource,
    selectOpts = {},
    _nodeId,
  } = props || {};

  const [activeKey, setActiveKey] = useState<string | undefined>();
  const selectOptsPrimaryKey = getFormattedValue(selectOpts?.primaryKey) as string;

  const isDesign = isDesignMode(props);
  let { _context } = props || {};

  if (isDesign) {
    _context = makeDesignContext();
  }
  const { _bindFilter } = filterConfig || {};
  let normalStyle = props.normalStyle || {};
  normalStyle = { ...normalStyle, ...oneCodePc };
  const { col = 1 } = normalStyle;

  const setDataSource = (data: any) => {
    if (data) {
      setDataToDs({
        _context,
        _dataSource,
        _dataSourceName,
        data: {
          ...data,
          refreshData: sendRequest,
        },
      });
    }
  };

  const refreshActive = ({
    data,
    currentActiveKey,
    isInit = false,
  }: {
    data: any;
    currentActiveKey?: string;
    isInit?: boolean;
  }) => {
    setDataSource({
      [__statisticCard_activeKey__]: currentActiveKey,
      [__statisticCard_currentItem__]: data,
      refreshData: sendRequest,
    });
    setTimeout(() => {
      if (!isDesign && currentActiveKey) {
        getLowCodeCoreUtil('util.executeEventWithoutJS')?.({
          eventType: 'onStatisticCardClick',
          events,
          _context,
          position: getLowCodeCoreUtil('enumMap.ButtonPosition.statisticCardEvent'),
          recordDataSource: {},
        });
      }
      if (isInit) {
        emitEvent(CnFilterDefaultValueFinished, {
          componentProps: props,
        });
      } else {
        emitEvent(CnFilterOnSearch, {
          componentProps: props,
          payload: {},
        });
      }
    }, 0);
  };

  const {
    data = [],
    loading,
    refreshData: sendRequest,
  } = useData({
    config: {
      ...props,
      _context,
      isDesign,
    },
    source: 'chart',
  });

  const { requestConfig } = dataFrom || {};

  useEffect(() => {
    if (data?.length) {
      if (selectOpts?.mode && selectOpts?.mode === 'single') {
        const defaultActiveKey = getFormattedValue(selectOpts?.defaultActiveKey, data) as string;
        const selector = data?.filter(
          (item: any) =>
            get(item, selectOptsPrimaryKey, null) &&
            get(item, selectOptsPrimaryKey, null) === defaultActiveKey,
        )[0];
        setActiveKey(defaultActiveKey);
        refreshActive({ data: selector, currentActiveKey: defaultActiveKey, isInit: true });
      }
    }
    if (data) {
      if (!isDesign) {
        getLowCodeCoreUtil('util.executeEventWithoutJS')?.({
          eventType: 'onStatisticCardRequestFinish',
          events,
          _context,
          position: getLowCodeCoreUtil('enumMap.ButtonPosition.statisticCardEvent'),
          recordDataSource: {},
        });
      }
    }
  }, [data]);

  let realData = data;
  if (dataFilterFun && realData) {
    try {
      realData = dataFilterFun(realData);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (isDesign) {
      sendRequest();
    }
  }, [requestConfig?.__datasourceName__]);

  useImperativeHandle(ref, () => ({
    load: sendRequest,
  }));

  useFilterSearchListener({
    _bindFilter,
    sendRequest,
    _context,
    requestConfig,
  });

  useStopLoop({
    _nodeId,
  });

  useEffect(() => {
    calculateFeature({
      props,
      options: {},
      position: FeaturePosition.statisticCard,
      sendRequest,
    });
  }, []);

  const layoutConfig = {
    columnGap: 8,
    rowGap: 8,
    columns: col || 4,
  };

  const length = (realData && realData.length) || 0;

  const { columnGap, rowGap, columns } = layoutConfig;
  const rowCount = Math.ceil(length / columns);
  const rows = initArray(rowCount);

  if (isDesign && !isArrayNotEmpty(realData)) {
    return (
      <WrapperContainer className="cn-statistic-card-list" nodeId={_nodeId}>
        <CnStatisticCardPro hideBorder={normalStyle?.hideBorder}>
          <CnStatisticCardListContext.Provider value={{ item: null, index: 0, blocks }} key={0}>
            {children}
          </CnStatisticCardListContext.Provider>
        </CnStatisticCardPro>
      </WrapperContainer>
    );
  }

  const onItemClick = (item: any) => () => {
    const selectActiveKey = get(item, selectOptsPrimaryKey, null);

    if (!selectOpts?.mode || selectOpts?.mode === 'none') {
      setDataSource({
        [__statisticCard_clickItem__]: item,
      });
      if (!isDesign) {
        getLowCodeCoreUtil('util.executeEventWithoutJS')?.({
          eventType: 'onStatisticCardItemClick',
          events,
          _context,
          position: getLowCodeCoreUtil('enumMap.ButtonPosition.statisticCardEvent'),
          recordDataSource: {},
        });
      }
    } else if (selectOpts?.clearable && activeKey === selectActiveKey) {
      setActiveKey(undefined);
      refreshActive({
        data: null,
        currentActiveKey: undefined,
      });
    } else if (selectActiveKey) {
      setActiveKey(selectActiveKey);
      refreshActive({
        data: item,
        currentActiveKey: selectActiveKey,
      });
    }
  };

  const isActive = (item: any) => {
    const itemKey = get(item, selectOptsPrimaryKey, null);
    if (selectOpts?.mode === 'single') {
      if (itemKey && activeKey && itemKey === activeKey) {
        return true;
      }
    }
    return false;
  };

  return (
    <WrapperContainer
      className="cn-statistic-card-list"
      loading={loading}
      skeletonVisible={!realData && skeleton.open}
      skeletonNode={
        <Skeleton flexLayoutConfig={layoutConfig} contentLength={skeleton.contentLength} />
      }
      nodeId={_nodeId}
    >
      <CnBox spacing={rowGap} direction="column">
        {rows.map((index) => {
          return (
            <CnBox spacing={columnGap} direction="row" key={index} style={{ overflow: 'auto' }}>
              {realData
                ?.filter(
                  (item: any, idx: number) =>
                    idx >= index * columns && idx < index * columns + columns,
                )
                .map((item: any) => {
                  return (
                    <CnStatisticCardPro
                      style={{
                        flexGrow: 0,
                        width: `calc((100% - ${columnGap * (columns - 1)}px)/${columns})`,
                        backgroundColor: getFormattedValue(
                          normalStyle.backgroundColor,
                          item,
                          index,
                        ) as string,
                      }}
                      hideBorder={normalStyle?.hideBorder}
                      onClick={onItemClick(item)}
                      active={isActive(item)}
                      canClick={
                        !!events?.find?.((im: any) => im.name === 'onStatisticCardItemClick')
                      }
                    >
                      <CnStatisticCardListContext.Provider
                        value={{ item, index, blocks }}
                        key={index}
                      >
                        {children}
                      </CnStatisticCardListContext.Provider>
                    </CnStatisticCardPro>
                  );
                })}
            </CnBox>
          );
        })}
      </CnBox>
    </WrapperContainer>
  );
});

CnStatisticCardListView.displayName = 'CnStatisticCardListView';

const CnStatisticCardListContent = forwardRef((props: any, ref) => {
  if (typeof useCreatePluginInstance !== 'function' || typeof HocBaseComponents !== 'function') {
    return <CnStatisticCardListView {...props} ref={ref} />;
  }

  const pluginIns = useCreatePluginInstance('CnStatisticCardList', props?.usePlugin);
  const plugins = pluginIns?.getPlugin?.();

  if (plugins?.length === 0) {
    return <CnStatisticCardListView {...props} ref={ref} />;
  }

  return HocBaseComponents(CnStatisticCardListView, {
    props,
    plugins,
    ref,
  });
});

CnStatisticCardListContent.displayName = 'CnStatisticCardListContent';

class CnStatisticCardList extends ViewWrapper {
  constructor(props: any) {
    super(props);

    this.ref = React.createRef();
  }

  render() {
    return <CnStatisticCardListContent {...this.props} ref={this.ref} />;
  }
}

CnStatisticCardList.displayName = 'CnStatisticCardList';

const CnStatisticLeftSide = (props: any) => {
  const { _context, children } = props;
  const context = useContext(CnStatisticCardListContext);
  if (context?.blocks?.leftSide === false) {
    return null;
  }
  return (
    <CnStatisticCardPro.Side align="top">
      {handleFormatChildren(children, {
        ...React.useContext(CnStatisticCardListContext),
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Side>
  );
};
CnStatisticLeftSide.displayName = 'CnStatisticLeftSide';

const CnStatisticRightSide = (props: any) => {
  const { _context, children } = props;
  const context = useContext(CnStatisticCardListContext);
  if (context?.blocks?.rightSide === false) {
    return null;
  }
  return (
    <CnStatisticCardPro.Side align="center">
      {handleFormatChildren(children, {
        ...React.useContext(CnStatisticCardListContext),
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Side>
  );
};
CnStatisticRightSide.displayName = 'CnStatisticRightSide';

const CnStatisticContent = (props: any) => {
  const { _context, children } = props;

  return (
    <CnStatisticCardPro.Content>
      {handleFormatChildren(children, {
        ...React.useContext(CnStatisticCardListContext),
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Content>
  );
};
CnStatisticContent.displayName = 'CnStatisticContent';

const CnStatisticHeader = (props: any) => {
  const { _context, children } = props;

  return (
    <CnStatisticCardPro.Header>
      {handleFormatChildren(children, {
        ...React.useContext(CnStatisticCardListContext),
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Header>
  );
};
CnStatisticHeader.displayName = 'CnStatisticHeader';

const CnStatisticTitle = (props: any) => {
  const { _context, children } = props;
  return (
    <CnStatisticCardPro.Title>
      {handleFormatChildren(children, {
        ...React.useContext(CnStatisticCardListContext),
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Title>
  );
};
CnStatisticTitle.displayName = 'CnStatisticTitle';

const CnStatisticAction = (props: any) => {
  const { _context, children } = props;
  const context = useContext(CnStatisticCardListContext);
  if (context?.blocks?.action === false) {
    return null;
  }
  return (
    <CnStatisticCardPro.Action>
      {handleFormatChildren(children, {
        ...context,
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Action>
  );
};
CnStatisticAction.displayName = 'CnStatisticAction';

const CnStatisticValue = (props: any) => {
  const { _context, children } = props;
  const context = useContext(CnStatisticCardListContext);
  if (context?.blocks?.mainValue === false) {
    return null;
  }
  return (
    <CnStatisticCardPro.MainValue>
      {handleFormatChildren(children, {
        ...context,
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.MainValue>
  );
};
CnStatisticValue.displayName = 'CnStatisticValue';

const CnStatisticPeriod = (props: any) => {
  const { _context, children } = props;
  const context = useContext(CnStatisticCardListContext);
  if (context?.blocks?.secondary1 === false) {
    return null;
  }
  return (
    <CnStatisticCardPro.Secondary>
      {handleFormatChildren(children, {
        ...context,
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Secondary>
  );
};
CnStatisticPeriod.displayName = 'CnStatisticPeriod';

const CnStatisticCompare = (props: any) => {
  const { _context, children } = props;
  const context = useContext(CnStatisticCardListContext);
  if (context?.blocks?.secondary2 === false) {
    return null;
  }
  return (
    <CnStatisticCardPro.Secondary>
      {handleFormatChildren(children, {
        ...context,
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Secondary>
  );
};
CnStatisticCompare.displayName = 'CnStatisticCompare';

const CnStatisticProgress = (props: any) => {
  const { _context, children } = props;

  return (
    <CnStatisticCardPro.Secondary>
      {handleFormatChildren(children, {
        ...React.useContext(CnStatisticCardListContext),
        state: _context?.state,
        _context,
      })}
    </CnStatisticCardPro.Secondary>
  );
};
CnStatisticProgress.displayName = 'CnStatisticProgress';

export default [
  CnStatisticCardList,
  CnStatisticLeftSide,
  CnStatisticRightSide,
  CnStatisticContent,
  CnStatisticHeader,
  CnStatisticTitle,
  CnStatisticAction,
  CnStatisticValue,
  CnStatisticPeriod,
  CnStatisticCompare,
  CnStatisticProgress,
];

export {
  CnStatisticCardList,
  CnStatisticLeftSide,
  CnStatisticRightSide,
  CnStatisticContent,
  CnStatisticHeader,
  CnStatisticTitle,
  CnStatisticAction,
  CnStatisticValue,
  CnStatisticPeriod,
  CnStatisticCompare,
  CnStatisticProgress,
};
